import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { showError, showSuccess } from "../../utils/sweetAlert";
import { firstError, resetPasswordAPI } from "../../utils/api";
import "./Login.css";

// Opened from the reset email: /reset-password?token=…&email=…
function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const email = params.get("email") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 8) {
      setFormError("Use at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setFormError("The two passwords don't match.");
      return;
    }

    setIsLoading(true);
    setFormError("");
    try {
      const data = await resetPasswordAPI({ token, email, password, passwordConfirmation: confirm });
      if (data?.status === 200) {
        showSuccess("Sign in with your new password.", "Password changed");
        navigate("/login", { replace: true });
      } else {
        setFormError(firstError(data, "This reset link is invalid or has expired. Request a new one."));
      }
    } catch {
      showError("We couldn't reach the server. Check your connection and try again.", "Connection problem");
    } finally {
      setIsLoading(false);
    }
  };

  const linkBroken = !token || !email;

  return (
    <div className="login-page">
      <main className="login-panel login-panel-full">
        <div className="login-card">
          <div className="login-header">
            <img src="/logo.png" alt="Jupiter Institute" className="login-logo" />
            <h1>Choose a new password</h1>
            <p>{linkBroken ? "This link is incomplete." : `For ${email}`}</p>
          </div>

          {(formError || linkBroken) && (
            <div className="alert alert-danger login-alert" role="alert">
              <i className="bi bi-exclamation-circle"></i>
              <div className="alert-body">
                {linkBroken ? "Open the link from the email again, or request a new one from the sign-in page." : formError}
              </div>
            </div>
          )}

          {!linkBroken && (
            <form onSubmit={handleSubmit} className="login-form" noValidate>
              <div className="form-group">
                <label htmlFor="new-password">New password</label>
                <div className="input-icon password-field">
                  <i className="bi bi-lock"></i>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="new-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setFormError("");
                    }}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    autoFocus
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    tabIndex={-1}
                  >
                    <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="confirm-password">Confirm new password</label>
                <div className="input-icon">
                  <i className="bi bi-lock"></i>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="confirm-password"
                    value={confirm}
                    onChange={(e) => {
                      setConfirm(e.target.value);
                      setFormError("");
                    }}
                    autoComplete="new-password"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <button type="submit" className="btn-primary btn-lg btn-block" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <span className="btn-spinner"></span> Saving…
                  </>
                ) : (
                  "Save new password"
                )}
              </button>
            </form>
          )}

          <Link to="/login" className="login-link login-back">
            <i className="bi bi-arrow-left"></i> Back to sign in
          </Link>
        </div>
      </main>
    </div>
  );
}

export default ResetPassword;
